import React from 'react';

import { Trans } from 'react-i18next';
import { APPLY_MANAGER_URL, APPLY_SERVICE_URL } from 'shared/constants';
import { useLangNavigate } from 'shared/hooks';

import { IframeDialog } from './IframeDialog';

export type FaqItem = {
  id: number;
  title: string;
  content: React.ReactNode;
};

export type FaqSection = {
  id: 'booking' | 'reservation' | 'moreHelp';
  title: string;
  faqs: FaqItem[];
};

function LangLink({
  to,
  className,
  onClick,
  children,
}: {
  to: string;
  className?: string;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  const { navigateWithLang } = useLangNavigate();

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
        navigateWithLang(to);
      }}
    >
      {children}
    </button>
  );
}

// HTML 태그/개행을 제거해 JSON-LD answer 용 평문으로 변환
const toPlainText = (value: string): string =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildQuestion = (question: string, answer: string) => ({
  '@type': 'Question',
  name: toPlainText(question),
  acceptedAnswer: { '@type': 'Answer', text: toPlainText(answer) },
});

/**
 * 가이드 페이지 FAQ 의 FAQPage 구조화 데이터(JSON-LD). createFaqSections 와 동일한
 * i18n 키를 읽어 화면 내용과 일치시킨다. moreHelp 답변은 화면처럼 부분 문구를 이어붙인다.
 */
export const getFaqJsonLd = (t: (key: string) => string) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    buildQuestion(t('sections.booking.faq1.question'), t('sections.booking.faq1.answer')),
    buildQuestion(t('sections.booking.faq2.question'), t('sections.booking.faq2.answer')),
    buildQuestion(t('sections.booking.faq3.question'), t('sections.booking.faq3.answer')),
    buildQuestion(t('sections.booking.faq4.question'), t('sections.booking.faq4.answer')),
    buildQuestion(t('sections.booking.faq5.question'), t('sections.booking.faq5.answer')),
    buildQuestion(t('sections.reservation.faq1.question'), t('sections.reservation.faq1.answer')),
    buildQuestion(t('sections.reservation.faq2.question'), t('sections.reservation.faq2.answer')),
    buildQuestion(t('sections.reservation.faq3.question'), t('sections.reservation.faq3.answer')),
    buildQuestion(
      t('sections.moreHelp.faq1.question'),
      `${t('sections.moreHelp.faq1.answer1')} ${t('sections.moreHelp.faq1.clickToApply')}`,
    ),
    buildQuestion(
      t('sections.moreHelp.faq2.question'),
      `${t('sections.moreHelp.faq2.answer1')} ${t('sections.moreHelp.faq2.goToApply')} ${t('sections.moreHelp.faq2.answer2')}`,
    ),
    buildQuestion(
      t('sections.moreHelp.faq3.question'),
      `${t('sections.moreHelp.faq3.answer1')} ${t('sections.moreHelp.faq3.goToApplyManager')} ${t('sections.moreHelp.faq3.answer2')}`,
    ),
  ],
});

export const createFaqSections = (t: (key: string) => string): FaqSection[] => [
  {
    id: 'booking',
    title: t('sections.booking.title'),
    faqs: [
      {
        id: 1,
        title: t('sections.booking.faq1.question'),
        content: t('sections.booking.faq1.answer'),
      },
      {
        id: 2,
        title: t('sections.booking.faq2.question'),
        content: (
          <Trans
            i18nKey="guide:sections.booking.faq2.answer"
            components={{ span: <span className="text-primary" />, strong: <strong />, br: <br /> }}
          />
        ),
      },
      {
        id: 3,
        title: t('sections.booking.faq3.question'),
        content: t('sections.booking.faq3.answer'),
      },
      {
        id: 4,
        title: t('sections.booking.faq4.question'),
        content: (
          <Trans
            i18nKey="guide:sections.booking.faq4.answer"
            components={{ strong: <strong />, br: <br />, span: <span className="text-primary" /> }}
          />
        ),
      },
      {
        id: 5,
        title: t('sections.booking.faq5.question'),
        content: <Trans i18nKey="guide:sections.booking.faq5.answer" components={{ strong: <strong /> }} />,
      },
    ],
  },
  {
    id: 'reservation',
    title: t('sections.reservation.title'),
    faqs: [
      {
        id: 6,
        title: t('sections.reservation.faq1.question'),
        content: (
          <Trans i18nKey="guide:sections.reservation.faq1.answer" components={{ strong: <strong />, br: <br /> }} />
        ),
      },
      {
        id: 7,
        title: t('sections.reservation.faq2.question'),
        content: <Trans i18nKey="guide:sections.reservation.faq2.answer" components={{ strong: <strong /> }} />,
      },
      {
        id: 8,
        title: t('sections.reservation.faq3.question'),
        content: <Trans i18nKey="guide:sections.reservation.faq3.answer" components={{ strong: <strong /> }} />,
      },
    ],
  },
  {
    id: 'moreHelp',
    title: t('sections.moreHelp.title'),
    faqs: [
      {
        id: 9,
        title: t('sections.moreHelp.faq1.question'),
        content: (
          <span className="text-primary">
            <Trans
              i18nKey="guide:sections.moreHelp.faq1.answer1"
              components={{
                span: <span />,
                strong: <strong />,
              }}
            />
            <LangLink
              to="/request-room"
              className="cursor-pointer text-primary underline underline-offset-2"
              onClick={() => window.dataLayer?.push({ event: 'click_cta', menu: 'Request a Room', section: 'Guide' })}
            >
              {t('sections.moreHelp.faq1.clickToApply')}
            </LangLink>
          </span>
        ),
      },
      {
        id: 10,
        title: t('sections.moreHelp.faq2.question'),
        content: (
          <>
            {t('sections.moreHelp.faq2.answer1')}{' '}
            <IframeDialog title="Apply for Service" src={APPLY_SERVICE_URL}>
              <span
                className="cursor-pointer text-primary underline underline-offset-2"
                onClick={() => window.dataLayer?.push({ event: 'click_cta', menu: 'Check With You', section: 'Guide' })}
              >
                {t('sections.moreHelp.faq2.goToApply')}
              </span>
            </IframeDialog>{' '}
            {t('sections.moreHelp.faq2.answer2')}
          </>
        ),
      },
      {
        id: 11,
        title: t('sections.moreHelp.faq3.question'),
        content: (
          <>
            <Trans
              i18nKey="guide:sections.moreHelp.faq3.answer1"
              components={{ span: <span className="text-primary" />, strong: <strong /> }}
            />{' '}
            <IframeDialog title="Apply for Service" src={APPLY_MANAGER_URL}>
              <span
                className="cursor-pointer underline underline-offset-2"
                onClick={() =>
                  window.dataLayer?.push({ event: 'click_cta', menu: 'Contract Accompaniment', section: 'Guide' })
                }
              >
                {t('sections.moreHelp.faq3.goToApplyManager')}
              </span>
            </IframeDialog>{' '}
            {t('sections.moreHelp.faq3.answer2')}
          </>
        ),
      },
    ],
  },
];
